// Реестр экранов. Каждая область — файл src/pages/<area>/routes.tsx (рекурсивно), который экспортирует:
//   routes        — маршруты внутри оболочки (меню, баннеры), только после входа;
//   nav           — пункты меню (NavItem);
//   bareRoutes    — после входа, но без оболочки (полноэкранные: согласие OAuth и т. п.);
//   publicRoutes  — без входа и без оболочки (/login, /setup).
// Общий роутер (router.tsx) собирает их сам: новый экран — новый файл, роутер не трогать.

import type { RouteObject } from 'react-router-dom'

import type { IconName } from '@/components/ui/Icon'

export type NavItem = {
  path: string
  label: string
  icon: IconName
  /** 'tab' — вкладка нижней панели телефона, 'more' — в «Ещё», null — только на десктопе/внутри раздела. */
  mobile: 'tab' | 'more' | null
  order: number
  /** Путь раздела, внутри которого пункт становится вкладкой: '/health', '/settings'. */
  parent?: string
}

export type PageModule = {
  routes?: RouteObject[]
  nav?: NavItem[]
  bareRoutes?: RouteObject[]
  publicRoutes?: RouteObject[]
}

export type CollectedPages = {
  routes: RouteObject[]
  bareRoutes: RouteObject[]
  publicRoutes: RouteObject[]
  nav: NavItem[]
}

export const MAX_MOBILE_TABS = 4

const byOrder = (a: NavItem, b: NavItem) => a.order - b.order || a.label.localeCompare(b.label, 'ru')

export function collectPages(modules: Record<string, PageModule>): CollectedPages {
  const result: CollectedPages = { routes: [], bareRoutes: [], publicRoutes: [], nav: [] }
  const seen = new Set<string>()
  for (const key of Object.keys(modules).sort()) {
    const mod = modules[key]
    if (!mod) continue
    result.routes.push(...(mod.routes ?? []))
    result.bareRoutes.push(...(mod.bareRoutes ?? []))
    result.publicRoutes.push(...(mod.publicRoutes ?? []))
    for (const item of mod.nav ?? []) {
      if (seen.has(item.path)) continue
      seen.add(item.path)
      result.nav.push(item)
    }
  }
  result.nav.sort(byOrder)
  return result
}

export type NavTree = {
  /** Пункты верхнего уровня (без parent) — боковое меню. */
  top: NavItem[]
  /** Вкладки разделов: parent → пункты. */
  children: Record<string, NavItem[]>
  /** Нижняя панель телефона (без «Ещё»). */
  tabs: NavItem[]
  /** Экран «Ещё». */
  more: NavItem[]
}

export function buildNav(items: NavItem[]): NavTree {
  const sorted = [...items].sort(byOrder)
  const top = sorted.filter((i) => !i.parent)
  const children: Record<string, NavItem[]> = {}
  for (const item of sorted) {
    if (!item.parent) continue
    ;(children[item.parent] ??= []).push(item)
  }
  return {
    top,
    children,
    tabs: top.filter((i) => i.mobile === 'tab').slice(0, MAX_MOBILE_TABS),
    more: top.filter((i) => i.mobile === 'more'),
  }
}

/** Раздел, к которому относится адрес: '/settings/security' → '/settings'. */
export function sectionOf(pathname: string, tree: NavTree): string | null {
  let best: string | null = null
  for (const parent of Object.keys(tree.children)) {
    if (pathname === parent || pathname.startsWith(`${parent}/`)) {
      if (!best || parent.length > best.length) best = parent
    }
  }
  return best
}

/** Активен ли пункт меню для адреса ('/' — только точное совпадение). */
export function isActivePath(itemPath: string, pathname: string): boolean {
  if (itemPath === '/') return pathname === '/'
  return pathname === itemPath || pathname.startsWith(`${itemPath}/`)
}
